/**
 * PlateCard — a single task card, shared by the List and Board views.
 *
 * Shows entity glyph, title, priority, status, due chip, assignees, and
 * comment/subtask counts. A left accent bar carries priority colour so the
 * urgent stuff pops when scanning. Completing is a one-tap checkbox.
 */

import { motion } from 'framer-motion';
import { Check, MessageSquare, GitBranch, CalendarClock, Paperclip } from 'lucide-react';
import type { TodoItem } from '@/types/schema';
import { getPriorityTheme, getStatusTheme, getEntityTheme } from '@/config/entityThemes';
import { EntityIcon } from '@/components/common/EntityIcon';
import { PriorityBadge } from '@/components/common/PriorityBadge';
import { AssigneeStack } from '@/components/common/AssigneeStack';
import { getLocaleSync } from '@/utils/locale';

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDue(dueDate: string): { label: string; tone: 'overdue' | 'today' | 'future' } {
  const today = todayStr();
  const tone = dueDate < today ? 'overdue' : dueDate === today ? 'today' : 'future';
  if (tone === 'today') return { label: 'Today', tone };
  const [y, m, d] = dueDate.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const label = date.toLocaleDateString(getLocaleSync(), { day: 'numeric', month: 'short' });
  return { label, tone };
}

interface PlateCardProps {
  task: TodoItem;
  onToggleComplete: (task: TodoItem, done: boolean) => void;
  onOpen?: (task: TodoItem) => void;
  /** Board view sets this so the card reads as grabbable; the draggable <div>
   *  wrapper lives in PlateBoardView (native HTML5 DnD, not framer's gesture). */
  grabbable?: boolean;
}

export function PlateCard({ task, onToggleComplete, onOpen, grabbable = false }: PlateCardProps) {
  const isDone = task.status === 'done';
  const priorityTheme = getPriorityTheme(task.priority);
  const statusTheme = getStatusTheme(task.status);
  const entityTheme = getEntityTheme(task.entityKind);
  const due = task.dueDate ? formatDue(task.dueDate) : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.18 }}
      className={`group relative rounded-xl border border-theme bg-surface-card hover:bg-surface-hover transition-colors overflow-hidden ${
        grabbable ? 'cursor-grab active:cursor-grabbing' : ''
      } ${isDone ? 'opacity-60' : ''}`}
    >
      {/* Priority accent rail */}
      <span className={`absolute left-0 top-2 bottom-2 w-1 rounded-r-full ${priorityTheme.accentBar}`} aria-hidden="true" />

      <div className="flex items-start gap-2.5 p-3 pl-4">
        {/* Complete checkbox — 18px visual inside a padded hit area so the
            touch target clears ~40px without shifting the layout */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleComplete(task, !isDone);
          }}
          className="-m-2.5 -mt-2 p-2.5 shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
          aria-label={isDone ? 'Mark not done' : 'Mark done'}
        >
          <span
            className={`w-[18px] h-[18px] rounded-full border flex items-center justify-center transition-colors ${
              isDone
                ? 'bg-emerald-500 border-emerald-500 text-white'
                : 'border-theme hover:border-emerald-400 text-transparent hover:text-emerald-400'
            }`}
          >
            <Check className="w-3 h-3" strokeWidth={3} />
          </span>
        </button>

        {/* Body */}
        <button
          type="button"
          onClick={() => onOpen?.(task)}
          className="flex-1 min-w-0 text-left rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary/40"
        >
          <div className="flex items-start gap-2">
            <EntityIcon kind={task.entityKind} size="sm" />
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-semibold leading-snug ${
                  isDone ? 'line-through text-theme-muted' : 'text-theme-primary'
                }`}
              >
                {task.title}
              </p>
              {task.sourceLabel && (
                <p className={`text-[11px] mt-0.5 truncate ${entityTheme.text}`}>
                  {entityTheme.label} · {task.sourceLabel}
                </p>
              )}
            </div>
          </div>

          {/* Meta row */}
          <div className="flex items-center flex-wrap gap-1.5 mt-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${statusTheme.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${statusTheme.dot}`} />
              {statusTheme.label}
            </span>
            {task.priority && task.priority !== 'normal' && (
              <PriorityBadge priority={task.priority} variant="badge" />
            )}
            {due && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${
                  due.tone === 'overdue'
                    ? 'bg-red-500/15 text-red-400'
                    : due.tone === 'today'
                    ? 'bg-amber-500/15 text-amber-400'
                    : 'bg-surface-raised text-theme-muted'
                }`}
              >
                <CalendarClock className="w-3 h-3" />
                {due.tone === 'overdue' ? `Overdue · ${due.label}` : due.label}
              </span>
            )}
            {!!task.commentCount && (
              <span className="inline-flex items-center gap-1 text-[10px] text-theme-muted">
                <MessageSquare className="w-3 h-3" />
                {task.commentCount}
              </span>
            )}
            {!!task.subtaskCount && (
              <span className="inline-flex items-center gap-1 text-[10px] text-theme-muted">
                <GitBranch className="w-3 h-3" />
                {task.subtaskDoneCount ?? 0}/{task.subtaskCount}
              </span>
            )}
            {!!task.tags?.length && (
              <span className="inline-flex items-center gap-1 text-[10px] text-theme-muted">
                <Paperclip className="w-3 h-3" />
                {task.tags.length}
              </span>
            )}
          </div>
        </button>

        {/* Assignees */}
        <div className="shrink-0 pt-0.5">
          <AssigneeStack assignees={task.assignees ?? []} size="sm" max={3} />
        </div>
      </div>
    </motion.div>
  );
}
